/**
 * Parse a comma-separated tags string into trimmed non-empty tags.
 */
export const parseMediaTags = (raw: string): string[] => {
  return raw
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
};

/**
 * Return unique tags (case-insensitive), preserving first-seen casing/order.
 */
export const uniqueMediaTags = (tags: string[]): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];

  for (const tag of tags) {
    const key = tag.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(tag);
  }

  return unique;
};

export const parseUniqueMediaTags = (raw: string): string[] => {
  return uniqueMediaTags(parseMediaTags(raw));
};

/**
 * Format rules: each tag must start with a single # and have at least one character after it.
 * Trailing empty segment after a trailing comma is ignored (e.g. "#a, #b,").
 */
export const isValidMediaTagsFormat = (raw: string): boolean => {
  if (!raw) {
    return true;
  }

  const parts = raw.split(',');
  if (parts.length > 0 && parts.at(-1)?.trim() === '') {
    parts.pop();
  }
  if (parts.length === 0) {
    return false;
  }

  for (const part of parts) {
    const tag = part.trim();
    if (!tag) {
      return false;
    }
    if (!tag.startsWith('#')) {
      return false;
    }
    if (tag.length === 1) {
      return false;
    }
    if ((tag.match(/#/g) || []).length > 1) {
      return false;
    }
  }

  return true;
};

export const hasDuplicateMediaTags = (raw: string): boolean => {
  const tags = parseMediaTags(raw);
  return uniqueMediaTags(tags).length !== tags.length;
};
